import React, { useState } from 'react';
import * as mui from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { SectionHeader, formatDate } from './UIComponents';
import HistoryIcon from '@mui/icons-material/History';
import RefreshIcon from '@mui/icons-material/Refresh';
import AssessmentIcon from '@mui/icons-material/Assessment';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import PeopleIcon from '@mui/icons-material/People';

const JobHistory = ({ 
  networkJobLogs, 
  fetchLogs 
}) => {
  const [loadingLogs, setLoadingLogs] = useState(false);

  const refreshLogs = async () => {
    setLoadingLogs(true);
    try {
      await fetchLogs();
    } catch (error) {
      console.error('Error refreshing logs:', error);
    } finally {
      setLoadingLogs(false);
    }
  };

  // Network job log columns for DataGrid
  const jobLogColumns = [
    { field: 'JOB_NAME', headerName: 'Job Name', flex: 1 },
    { 
      field: 'DOMAIN_NAME', 
      headerName: 'Domain', 
      flex: 1 
    },
    { 
      field: 'RUN_DATE', 
      headerName: 'Run Date', 
      width: 180,
      valueFormatter: (params) => params && params.value ? formatDate(params.value) : 'N/A'
    },
    { 
      field: 'STATUS', 
      headerName: 'Status', 
      width: 120,
      renderCell: (params) => (
        <mui.Chip 
          label={params.row.STATUS} 
          color={params.row.STATUS === 'SUCCESS' ? 'success' : 'error'} 
          size="small" 
        />
      )
    },
    { 
      field: 'AFFECTED_USERS', 
      headerName: 'Affected Users', 
      width: 130,
      type: 'number'
    },
    { 
      field: 'MESSAGE', 
      headerName: 'Message', 
      flex: 1 
    },
  ];

  return (
    <mui.Paper sx={{ p: 3, mb: 4 }}>
      <SectionHeader 
        title="Network Job History" 
        icon={<HistoryIcon />} 
        description="View history of network job executions" 
        action={
          <mui.Button 
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={refreshLogs}
            disabled={loadingLogs}
          >
            Refresh
          </mui.Button>
        }
      />
      
      {loadingLogs ? (
        <mui.Box sx={{ display: 'flex', justifyContent: 'center', my: 4, height: 400 }}>
          <mui.CircularProgress />
        </mui.Box>
      ) : networkJobLogs.length > 0 ? (
        <>
          <mui.Box sx={{ mb: 3 }}>
            <mui.Grid container spacing={2}>
              <mui.Grid item xs={12} sm={4}>
                <mui.Paper sx={{ p: 2, height: '100%', display: 'flex', alignItems: 'center', bgcolor: 'success.light' }}>
                  <mui.Box sx={{ mr: 2, color: 'white' }}>
                    <AssessmentIcon fontSize="large" />
                  </mui.Box>
                  <mui.Box>
                    <mui.Typography variant="h4" color="white" fontWeight="bold">
                      {networkJobLogs.filter(log => log.STATUS === 'SUCCESS').length}
                    </mui.Typography>
                    <mui.Typography variant="body2" color="white">
                      Successful Jobs
                    </mui.Typography>
                  </mui.Box>
                </mui.Paper>
              </mui.Grid>
              <mui.Grid item xs={12} sm={4}>
                <mui.Paper sx={{ p: 2, height: '100%', display: 'flex', alignItems: 'center', bgcolor: 'error.light' }}>
                  <mui.Box sx={{ mr: 2, color: 'white' }}>
                    <WarningAmberIcon fontSize="large" />
                  </mui.Box>
                  <mui.Box>
                    <mui.Typography variant="h4" color="white" fontWeight="bold">
                      {networkJobLogs.filter(log => log.STATUS !== 'SUCCESS').length}
                    </mui.Typography>
                    <mui.Typography variant="body2" color="white">
                      Failed Jobs
                    </mui.Typography>
                  </mui.Box>
                </mui.Paper>
              </mui.Grid>
              <mui.Grid item xs={12} sm={4}>
                <mui.Paper sx={{ p: 2, height: '100%', display: 'flex', alignItems: 'center', bgcolor: 'primary.light' }}>
                  <mui.Box sx={{ mr: 2, color: 'white' }}>
                    <PeopleIcon fontSize="large" />
                  </mui.Box>
                  <mui.Box>
                    <mui.Typography variant="h4" color="white" fontWeight="bold">
                      {networkJobLogs.reduce((sum, log) => sum + (log.AFFECTED_USERS || 0), 0)}
                    </mui.Typography>
                    <mui.Typography variant="body2" color="white">
                      Affected Users
                    </mui.Typography>
                  </mui.Box>
                </mui.Paper>
              </mui.Grid>
            </mui.Grid>
          </mui.Box>
          
          <mui.Box sx={{ height: 500, width: '100%' }}>
            <DataGrid
              rows={networkJobLogs}
              columns={jobLogColumns}
              pageSize={10}
              rowsPerPageOptions={[10, 25, 50]}
              disableSelectionOnClick
              getRowId={(row, index) => row.id || index}
            />
          </mui.Box>
        </>
      ) : (
        <mui.Box sx={{ p: 3, textAlign: 'center', height: 400, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
          <HistoryIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
          <mui.Typography variant="h6" gutterBottom>
            No Job History Available
          </mui.Typography>
          <mui.Typography variant="body2" color="text.secondary" sx={{ maxWidth: 500, mb: 3 }}>
            No network job executions have been recorded. Job history will be displayed once jobs have been executed.
          </mui.Typography>
        </mui.Box>
      )}
    </mui.Paper>
  );
};

export default JobHistory; 